/**
 * Página mestre de relatórios (§3.6/W3 da v4): seletor à esquerda (7
 * relatórios — "Atendentes" é da spec 015), barra de filtros + Exibir/Exportar CSV e a renderização do
 * relatório ativo à direita. No extrato, também o PDF (PoolTrack, 03/10): "Baixar
 * PDF" ao lado do CSV leva o filtro inteiro (resumo + um atendente por página
 * nova), e o botão PDF de cada linha "Total — <nome>" leva só aquela pessoa. Os filtros só são aplicados ao clicar em
 * Exibir (estado draft → applied); o CSV usa os dados já carregados — menos
 * o do extrato, que é paginado na tela e pede o período inteiro para exportar.
 *
 * O que está APLICADO mora na URL (relatório, filtros e página), no padrão da
 * lista de Atendimentos (spec 018) e da tela de clientes: abrir um ticket do
 * extrato e voltar devolve a mesma página dos mesmos filtros, e voltar/avançar
 * do navegador anda pelas páginas. O rascunho dos filtros segue local.
 *
 * PoolTrack, 02/10 ("separar cliente/associação e atendente"): o seletor de
 * atendentes trazia os 274 logins do portal — um por associação, com o nome
 * dela — misturados à equipe. Agora a equipe vem de GET /reports/filter-options
 * (só admin e atendente do tenant), e o cliente tem o seu seletor, com busca,
 * em todos os relatórios. O estado puro (URL, rascunho, rótulos) mora em
 * report-search.ts, onde a API o testa.
 */
import { useState, type ReactNode } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  ActivityIcon,
  BriefcaseIcon,
  ClockIcon,
  DownloadIcon,
  FileDownIcon,
  FolderTreeIcon,
  GaugeIcon,
  StarIcon,
  UserCheckIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { EmptyState, PageSpinner } from '@/components/ui/misc';
import { pageCountOf } from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { capitalize } from '@erp/shared';
import { isoDaysAgo, todayISO } from '@/features/billing/billing-format';
import { desksListQuery } from '@/features/tickets/tickets-queries';
import { teamListQuery } from '@/features/team/team-queries';
import { useTerms } from '@/shared/tenant-profile';
import {
  appointmentsExtractReportQuery,
  attendantsReportQuery,
  catalogReportQuery,
  downloadAppointmentsExtractPdf,
  EXTRACT_PAGE_SIZE,
  executiveReportQuery,
  peaksReportQuery,
  ratingsReportQuery,
  reportFilterOptionsQuery,
  workloadReportQuery,
} from './reports-queries';
import { downloadCsv } from './report-csv';
import { MultiFilter } from './report-filters';
import {
  allClientsLabel,
  appliedFrom,
  clientCountLabel,
  extractPdfAttendant,
  extractPdfErrorText,
  filtersKey,
  filtersToSearch,
  REPORT_FILTER_MAX,
  REPORT_FILTERS,
  sameFilters,
  visibleOptions,
  type ReportFilters,
  type ReportKey,
} from './report-search';
import { PeaksView, peaksCsv } from './report-peaks';
import { RatingsView, ratingsCsv } from './report-ratings';
import { AttendantsView, attendantsCsv } from './report-attendants';
import {
  CatalogView,
  ExtractView,
  ExecutiveView,
  WorkloadView,
  catalogCsv,
  executiveCsv,
  extractCsv,
  workloadCsv,
} from './report-views';

// ── Definição dos relatórios ────────────────────────────────────────────────

interface ReportDef {
  key: ReportKey;
  label: string;
  Icon: typeof ClockIcon;
  /** slug do arquivo CSV */
  slug: string;
}

// os filtros que cada um aceita moram em REPORT_FILTERS (report-search.ts)
const REPORTS: ReportDef[] = [
  {
    key: 'extract',
    label: 'Extrato de apontamentos',
    Icon: ClockIcon,
    slug: 'extrato-apontamentos',
  },
  // spec 015: pausa + conversas + tickets por pessoa (permissão reports.attendants)
  { key: 'attendants', label: 'Atendentes', Icon: UserCheckIcon, slug: 'atendentes' },
  { key: 'workload', label: 'Carga de trabalho', Icon: GaugeIcon, slug: 'carga-de-trabalho' },
  { key: 'peaks', label: 'Picos de atendimento', Icon: ActivityIcon, slug: 'picos-de-atendimento' },
  { key: 'executive', label: 'Executivo', Icon: BriefcaseIcon, slug: 'executivo' },
  {
    key: 'catalog',
    label: 'Catálogo de serviço',
    Icon: FolderTreeIcon,
    slug: 'catalogo-de-servico',
  },
  { key: 'ratings', label: 'Avaliações', Icon: StarIcon, slug: 'avaliacoes' },
];

// ── URL ─────────────────────────────────────────────────────────────────────

// o schema da URL é do report-search.ts (puro, testado pela API); reexportado
// para o router, que já o importava daqui
export { reportsSearchSchema, type ReportsSearch } from './report-search';

const routeApi = getRouteApi('/_authenticated/reports');

function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'error' in err) {
    return String((err as { error: unknown }).error);
  }
  return 'erro de rede';
}

// ── Página ──────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const search = routeApi.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const terms = useTerms();
  const report = search.report;
  const applied = appliedFrom(search, { start: isoDaysAgo(30), end: todayISO() });
  const [draft, setDraft] = useState<ReportFilters>(applied);
  // O aplicado mudou por fora (voltar/avançar, link colado): o rascunho
  // acompanha. Ajuste durante o render, sem efeito (padrão do React).
  const [draftBase, setDraftBase] = useState(filtersKey(applied));
  if (draftBase !== filtersKey(applied)) {
    setDraftBase(filtersKey(applied));
    setDraft(applied);
  }
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // extrato em PDF: 'all' = o filtro inteiro; um id = o PDF daquele atendente
  const [pdfBusy, setPdfBusy] = useState<'all' | number | null>(null);

  const def = REPORTS.find((r) => r.key === report) ?? REPORTS[0]!;
  const supports = REPORT_FILTERS[def.key];

  const desksQ = useQuery(desksListQuery());
  const optionsQ = useQuery(reportFilterOptionsQuery());
  // Interruptor do lado rápido (mudança de dois lados): o SPA publica antes da
  // API. Sem o endpoint novo, o seletor de atendentes cai na equipe do tenant
  // (GET /users/team, que tem o próprio recuo) e o de cliente não aparece — a
  // API velha ignoraria client_ids e a tela diria "filtrado" sobre o total.
  const legacyUsersQ = useQuery({
    ...teamListQuery({ includeInactive: true }),
    enabled: optionsQ.isError,
  });
  const clientFilterReady = optionsQ.isSuccess;

  const period = { start: applied.start, end: applied.end };
  const clientIds = applied.clientIds;
  const extractFilters = {
    ...period,
    userIds: applied.userIds,
    deskIds: applied.deskIds,
    clientIds,
  };
  const extractQ = useQuery({
    ...appointmentsExtractReportQuery({ ...extractFilters, page: search.page }),
    enabled: report === 'extract',
  });
  const attendantsQ = useQuery({
    ...attendantsReportQuery({ ...period, userIds: applied.userIds, clientIds }),
    enabled: report === 'attendants',
  });
  const workloadQ = useQuery({
    ...workloadReportQuery({ clientIds }),
    enabled: report === 'workload',
  });
  const peaksQ = useQuery({
    ...peaksReportQuery({ ...period, clientIds }),
    enabled: report === 'peaks',
  });
  const executiveQ = useQuery({
    ...executiveReportQuery({ ...period, deskIds: applied.deskIds, clientIds }),
    enabled: report === 'executive',
  });
  const catalogQ = useQuery({
    ...catalogReportQuery({ ...period, clientIds }),
    enabled: report === 'catalog',
  });
  const ratingsQ = useQuery({
    ...ratingsReportQuery({ ...period, deskIds: applied.deskIds, clientIds }),
    enabled: report === 'ratings',
  });

  const activeQ: UseQueryResult<unknown> = {
    extract: extractQ,
    attendants: attendantsQ,
    workload: workloadQ,
    peaks: peaksQ,
    executive: executiveQ,
    catalog: catalogQ,
    ratings: ratingsQ,
  }[report];

  function selectReport(key: ReportKey) {
    setExportError(null);
    void navigate({ to: '/reports', search: { ...search, report: key, page: 1 } });
  }

  function show() {
    // Mesmos filtros ⇒ a queryKey não muda; força um refetch explícito.
    if (sameFilters(draft, applied)) void activeQ.refetch();
    else void navigate({ to: '/reports', search: { report, ...filtersToSearch(draft), page: 1 } });
  }

  function goToExtractPage(page: number) {
    void navigate({ to: '/reports', search: { ...search, page } });
  }

  async function exportCsv() {
    const stamp = supports.period ? `${applied.start}_${applied.end}` : todayISO();
    const filename = `relatorio-${def.slug}_${stamp}.csv`;
    setExportError(null);
    if (report === 'extract') {
      // A tela mostra UMA página; a planilha é o período inteiro. Sem página
      // a API devolve todas as linhas — a mesma resposta de antes da paginação.
      setExporting(true);
      try {
        const full = await queryClient.fetchQuery(appointmentsExtractReportQuery(extractFilters));
        const table = extractCsv(full);
        downloadCsv(filename, table.headers, table.rows);
      } catch (err) {
        setExportError(`Erro ao exportar: ${errorMessage(err)}`);
      } finally {
        setExporting(false);
      }
      return;
    }
    const table =
      report === 'attendants' && attendantsQ.data
        ? attendantsCsv(attendantsQ.data)
        : report === 'workload' && workloadQ.data
          ? workloadCsv(workloadQ.data)
          : report === 'peaks' && peaksQ.data
            ? peaksCsv(peaksQ.data)
            : report === 'executive' && executiveQ.data
              ? executiveCsv(executiveQ.data)
              : report === 'catalog' && catalogQ.data
                ? catalogCsv(catalogQ.data)
                : report === 'ratings' && ratingsQ.data
                  ? ratingsCsv(ratingsQ.data)
                  : null;
    if (table) downloadCsv(filename, table.headers, table.rows);
  }

  /**
   * Extrato em PDF (PoolTrack, 03/10: "baixar em PDF o relatório de cada
   * atendente"). Sem `attendant`: o filtro APLICADO inteiro — um documento com
   * o resumo e cada atendente a partir de uma página nova. Com ele: só aquela
   * pessoa, nos mesmos período, mesas e clientes (o botão da linha de total).
   */
  async function downloadPdf(attendant?: { userId: number; userName: string }) {
    setExportError(null);
    setPdfBusy(attendant?.userId ?? 'all');
    try {
      await downloadAppointmentsExtractPdf({
        ...extractFilters,
        ...(attendant ? { userIds: [attendant.userId] } : {}),
        attendantName:
          attendant?.userName ?? extractPdfAttendant(extractQ.data?.totalsByUser ?? []),
      });
    } catch (err) {
      setExportError(extractPdfErrorText(err));
    } finally {
      setPdfBusy(null);
    }
  }

  const deskOptions = visibleOptions(
    (desksQ.data ?? []).map((d) => ({ id: d.id, name: d.displayName, active: d.active })),
    draft.deskIds
  );
  // só a equipe do tenant — pelas opções do relatório ou, sem elas, pela equipe
  const staff = optionsQ.data?.attendants ?? legacyUsersQ.data?.items ?? [];
  const userOptions = visibleOptions(staff, draft.userIds);
  const clientOptions = visibleOptions(optionsQ.data?.clients ?? [], draft.clientIds);

  return (
    <div className="space-y-4">
      <h1 className="text-xl leading-7 font-semibold">Relatórios</h1>

      <div className="flex flex-col gap-4 lg:flex-row">
        {/* Seletor de relatório */}
        <aside className="w-full shrink-0 self-start rounded-lg border bg-card p-2 shadow-xs lg:w-60">
          <nav className="flex flex-col gap-0.5" aria-label="Relatórios">
            {REPORTS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                aria-current={report === key ? 'page' : undefined}
                onClick={() => selectReport(key)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] font-medium transition-colors',
                  report === key
                    ? 'bg-primary/10 text-primary-pressed'
                    : 'text-foreground hover:bg-muted/60'
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Área do relatório */}
        <section className="min-w-0 flex-1 space-y-4">
          {/* Barra de filtros */}
          <div className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4 shadow-xs">
            {supports.period ? (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-start" className="text-[13px]">
                    Início
                  </Label>
                  <Input
                    id="rp-start"
                    type="date"
                    className="h-8 w-40"
                    value={draft.start}
                    onChange={(e) => {
                      if (e.target.value) setDraft({ ...draft, start: e.target.value });
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="rp-end" className="text-[13px]">
                    Fim
                  </Label>
                  <Input
                    id="rp-end"
                    type="date"
                    className="h-8 w-40"
                    value={draft.end}
                    onChange={(e) => {
                      if (e.target.value) setDraft({ ...draft, end: e.target.value });
                    }}
                  />
                </div>
              </>
            ) : (
              <p className="self-center text-[13px] text-muted-foreground">
                Foto atual das filas — sem filtro de período.
              </p>
            )}
            {supports.desks ? (
              <div className="flex flex-col gap-1.5">
                <Label className="text-[13px]">Mesas</Label>
                <MultiFilter
                  options={deskOptions}
                  selected={draft.deskIds}
                  onChange={(deskIds) => setDraft({ ...draft, deskIds })}
                  allLabel="Todas as mesas"
                  countLabel={(n) => (n === 1 ? '1 mesa' : `${n} mesas`)}
                  max={REPORT_FILTER_MAX}
                  ariaLabel="Mesas"
                />
              </div>
            ) : null}
            {supports.clients && clientFilterReady ? (
              <div className="flex flex-col gap-1.5">
                <Label className="text-[13px]">{capitalize(terms.client)}</Label>
                <MultiFilter
                  options={clientOptions}
                  selected={draft.clientIds}
                  onChange={(ids) => setDraft({ ...draft, clientIds: ids })}
                  allLabel={allClientsLabel(terms)}
                  countLabel={(n) => clientCountLabel(terms, n)}
                  searchable
                  searchPlaceholder={`Buscar ${terms.client}…`}
                  max={REPORT_FILTER_MAX}
                  ariaLabel={capitalize(terms.client)}
                />
              </div>
            ) : null}
            {supports.users ? (
              <div className="flex flex-col gap-1.5" data-tour="relatorios-atendentes">
                <Label className="text-[13px]">Atendentes</Label>
                <MultiFilter
                  options={userOptions}
                  selected={draft.userIds}
                  onChange={(userIds) => setDraft({ ...draft, userIds })}
                  allLabel="Todos os atendentes"
                  countLabel={(n) => (n === 1 ? '1 atendente' : `${n} atendentes`)}
                  searchable
                  searchPlaceholder="Buscar atendente…"
                  max={REPORT_FILTER_MAX}
                  ariaLabel="Atendentes"
                />
              </div>
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" onClick={show} disabled={activeQ.isFetching} data-tour="relatorios-exibir">
                Exibir
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void exportCsv()}
                disabled={!activeQ.isSuccess || exporting}
                title={
                  report === 'extract'
                    ? 'Exporta todas as linhas do período como CSV, não só a página'
                    : 'Exporta os dados carregados como CSV'
                }
              >
                <DownloadIcon />
                {exporting ? 'Exportando…' : 'Exportar CSV'}
              </Button>
              {report === 'extract' ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void downloadPdf()}
                  data-tour="relatorios-baixar-pdf"
                  disabled={!extractQ.isSuccess || pdfBusy !== null}
                  title="Baixa o período inteiro em PDF: um resumo e cada atendente a partir de uma página nova. Para um atendente só, use o botão PDF na linha de total dele."
                >
                  <FileDownIcon />
                  {pdfBusy === 'all' ? 'Gerando PDF…' : 'Baixar PDF'}
                </Button>
              ) : null}
            </div>
            {exportError ? (
              <p className="w-full text-right text-[13px] text-destructive">{exportError}</p>
            ) : null}
          </div>

          {/* Conteúdo */}
          <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
            <div className="flex items-center gap-2 border-b border-divider px-4 py-3">
              <def.Icon className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold">{def.label}</h2>
              {supports.period && activeQ.isSuccess ? (
                <span className="text-[13px] text-muted-foreground">
                  {new Date(`${applied.start}T00:00:00`).toLocaleDateString('pt-BR')} –{' '}
                  {new Date(`${applied.end}T00:00:00`).toLocaleDateString('pt-BR')}
                </span>
              ) : null}
            </div>
            {report === 'extract' ? (
              <ReportBody q={extractQ} isEmpty={(d) => (d.rowsTotal ?? d.rows.length) === 0}>
                {(d) => (
                  <ExtractView
                    data={d}
                    pdf={{
                      onDownload: (attendant) => void downloadPdf(attendant),
                      busyUserId: typeof pdfBusy === 'number' ? pdfBusy : null,
                      busy: pdfBusy !== null,
                    }}
                    pagination={{
                      page: search.page,
                      // sem rowsTotal a API é a de antes da paginação e
                      // mandou tudo: uma página só
                      pageCount:
                        d.rowsTotal === undefined ? 1 : pageCountOf(d.rowsTotal, EXTRACT_PAGE_SIZE),
                      total: d.rowsTotal ?? d.rows.length,
                      onPage: goToExtractPage,
                    }}
                  />
                )}
              </ReportBody>
            ) : report === 'attendants' ? (
              <ReportBody q={attendantsQ} isEmpty={(d) => d.rows.length === 0}>
                {(d) => <AttendantsView data={d} />}
              </ReportBody>
            ) : report === 'workload' ? (
              <ReportBody q={workloadQ} isEmpty={(d) => d.rows.length === 0}>
                {(d) => <WorkloadView data={d} />}
              </ReportBody>
            ) : report === 'peaks' ? (
              <ReportBody q={peaksQ} isEmpty={(d) => d.total === 0}>
                {(d) => <PeaksView data={d} />}
              </ReportBody>
            ) : report === 'executive' ? (
              <ReportBody q={executiveQ} isEmpty={(d) => d.rows.length === 0}>
                {(d) => <ExecutiveView data={d} />}
              </ReportBody>
            ) : report === 'catalog' ? (
              <ReportBody
                q={catalogQ}
                isEmpty={(d) => d.rows.length === 0 && d.withoutCatalog === 0}
              >
                {(d) => <CatalogView data={d} />}
              </ReportBody>
            ) : (
              <ReportBody q={ratingsQ} isEmpty={(d) => d.sent === 0 && d.responses === 0}>
                {(d) => <RatingsView data={d} />}
              </ReportBody>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Estados carregando/erro/vazio no padrão do app ──────────────────────────

function ReportBody<T>({
  q,
  isEmpty,
  children,
}: {
  q: UseQueryResult<T>;
  isEmpty: (data: T) => boolean;
  children: (data: T) => ReactNode;
}) {
  if (q.isPending) return <PageSpinner />;
  if (q.isError) {
    return <EmptyState message={`Erro ao carregar o relatório: ${errorMessage(q.error)}`} />;
  }
  if (isEmpty(q.data)) {
    return <EmptyState message="Sem dados para os filtros selecionados" />;
  }
  return <>{children(q.data)}</>;
}
